import React, { useEffect, useMemo, useState } from 'react';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError, showSuccess } from '../../../../core/alert/sweetAlertConfig';
import { LocationDTO, BuildingDTO, FloorDTO } from './ParkingSlotRegister';

interface UpdateParkingSlotModuleProps {
  show: boolean;
  onHide: () => void;
  parkingSlot: {
    id: number;
    slotName: string;
    x: string;
    y: string;
    locationId?: number | null;
    floorId?: number | null;
    locationName?: string | null;
    floorName?: string | null;
    status: string;
  };
  locations: LocationDTO[];
  buildings: BuildingDTO[];
  floors: FloorDTO[];
  onSuccess: () => void;
}

const UpdateParkingSlotModule: React.FC<UpdateParkingSlotModuleProps> = ({
  show,
  onHide,
  parkingSlot,
  locations,
  buildings,
  floors,
  onSuccess
}) => {
  const [slotName, setSlotName] = useState<string>(parkingSlot.slotName);
  const [status, setStatus] = useState<string>(parkingSlot.status);

  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(parkingSlot.locationId ?? null);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(parkingSlot.floorId ?? null);

  const [availableBuildings, setAvailableBuildings] = useState<BuildingDTO[]>(buildings || []);
  const [availableFloors, setAvailableFloors] = useState<FloorDTO[]>(floors || []);

  // derive initial building from initial floor
  const initialBuildingIdFromFloor = useMemo(() => {
    if (!parkingSlot.floorId) return null;
    const f = floors.find(fl => fl.id === parkingSlot.floorId);
    return f?.buildingId ?? null;
  }, [parkingSlot.floorId, floors]);

  // Load Buildings when Location changes (clear only if current building doesn't belong)
  useEffect(() => {
    const run = async () => {
      if (!selectedLocationId) {
        setAvailableBuildings([]);
        setAvailableFloors([]);
        setSelectedBuildingId(null);
        setSelectedFloorId(null);
        return;
      }

      try {
        const res = await apiDefinitions.getBuildingsByLocationId(selectedLocationId);
        if (res.status === 200) {
          const data: BuildingDTO[] = res.data?.data || [];
          setAvailableBuildings(data);

          // if current building not in this location → clear building & floor
          if (!data.some(b => b.id === selectedBuildingId)) {
            setSelectedBuildingId(null);
            setAvailableFloors([]);
            setSelectedFloorId(null);
          }
        } else {
          showError(res.data?.statusDescription || 'Failed to fetch buildings');
        }
      } catch (e: any) {
        showError(e?.response?.data?.statusDescription || 'Failed to fetch buildings');
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId]);

  // Load Floors when Building changes (clear only if current floor doesn't belong)
  useEffect(() => {
    const run = async () => {
      if (!selectedBuildingId) {
        setAvailableFloors([]);
        setSelectedFloorId(null);
        return;
      }
      try {
        const res = await apiDefinitions.getFloorsByBuildingId(selectedBuildingId);
        if (res.status === 200) {
          const data: FloorDTO[] = res.data?.data || [];
          setAvailableFloors(data);

          if (!data.some(fl => fl.id === selectedFloorId)) {
            setSelectedFloorId(null);
          }
        } else {
          showError(res.data?.statusDescription || 'Failed to fetch floors');
        }
      } catch (e: any) {
        showError(e?.response?.data?.statusDescription || 'Failed to fetch floors');
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuildingId]);

  // Initial prefill chain
  useEffect(() => {
    if (parkingSlot.floorId) {
      setSelectedFloorId(parkingSlot.floorId);
      if (initialBuildingIdFromFloor) setSelectedBuildingId(initialBuildingIdFromFloor);
    }
    if (parkingSlot.locationId) setSelectedLocationId(parkingSlot.locationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parkingSlot.id]);

  const handleSubmit = async () => {
    if (!slotName.trim()) {
      showError('Slot name is required');
      return;
    }

    // Most specific wins: floorId > locationId
    const payload: any = {
      id: parkingSlot.id,
      slotName,
      status,
      floorId: selectedFloorId ?? null,
      locationId: selectedFloorId ? null : (selectedLocationId ?? null)
    };

    try {
      const res = await apiDefinitions.updateParkingSlot(payload);
      if (res.status === 200) {
        showSuccess("Parking slot updated successfully");
        onSuccess();
        onHide();
      } else {
        showError(res.data?.statusDescription || 'Failed to update parking slot');
      }
    } catch (e: any) {
      showError(e?.response?.data?.statusDescription || 'Failed to update parking slot. Please try again.');
    }
  };

  return (
    <div className={`modal ${show ? 'd-block' : ''}`} tabIndex={-1}>
      <div className="modal-dialog">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title">Update Parking Slot</h5>
            <button type="button" className="btn-close" onClick={onHide}></button>
          </div>

          {/* Body */}
          <div className="modal-body">
            <div className="form-group mb-2">
              <label>Slot Name</label>
              <input
                type="text"
                className="form-control"
                value={slotName}
                onChange={e => setSlotName(e.target.value)}
              />
            </div>

            <div className="form-group mb-2">
              <label>Status</label>
              <select className="form-control" value={status} onChange={e => setStatus(e.target.value)}>
                <option value="AVAILABLE">Available</option>
                <option value="OCCUPIED">Occupied</option>
                <option value="RESERVED">Reserved</option>
              </select>
            </div>

            {/* <div className="form-group mb-2">
              <label>Location</label>
              <select
                className="form-control"
                value={selectedLocationId ?? ''}
                onChange={e => {
                  const val = e.target.value ? Number(e.target.value) : null;
                  setSelectedLocationId(val);
                  // downstream clearing handled in effects (only if invalid)
                }}
                // lock upstream if floor is chosen, to keep hierarchy consistent
                disabled={!!selectedFloorId}
              >
                <option value="">Select a location</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group mb-2">
              <label>Building</label>
              <select
                className="form-control"
                value={selectedBuildingId ?? ''}
                onChange={e => {
                  const val = e.target.value ? Number(e.target.value) : null;
                  setSelectedBuildingId(val);
                }}
                disabled={!selectedLocationId || !!selectedFloorId}
              >
                <option value="">Select a building</option>
                {availableBuildings.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Floor (Optional)</label>
              <select
                className="form-control"
                value={selectedFloorId ?? ''}
                onChange={e => {
                  const val = e.target.value ? Number(e.target.value) : null;
                  setSelectedFloorId(val);
                }}
                disabled={!selectedBuildingId}
              >
                <option value="">Select a floor</option>
                {availableFloors.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div> */}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button type="button" className="btn btn-primary" onClick={onHide}>
              Cancel
            </button>
            <button type="button" className="btn btn-success" onClick={handleSubmit}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateParkingSlotModule;
